import { createRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { publicLayoutRoute } from '@/ui/pages/public'
import { resolveLanguage } from '@/application/i18n/language'
import { CONTACT_EMAIL } from '@/application/i18n/constants'
import { useDocumentMeta } from '@/application/hooks/useDocumentMeta'
import { OG_IMAGE_URL } from '@/application/seo/site'
import { useLocalizedUrl } from '@/application/hooks/useLocalizedUrl'
import { LegalPageLayout } from '@/ui/pages/public/legal/LegalPageLayout'
import { formatLastUpdated } from '@/ui/pages/public/legal/lastUpdated'

export const confidentialiteRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: 'confidentialite',
  component: ConfidentialitePage,
})

/**
 * GDPR-compliant privacy policy: controller identity, data actually
 * collected (grounded in the backend's real tables/columns — see the domain
 * services this content mirrors), legal basis per purpose, retention,
 * recipients/processors, international transfer disclosure, data subject
 * rights, and the CNIL complaint procedure.
 */
export function ConfidentialitePage() {
  const { t, i18n } = useTranslation()
  const lang = resolveLanguage(i18n.language)

  const pageUrl = useLocalizedUrl('/confidentialite')

  useDocumentMeta({
    title: t('seo.confidentialite_title'),
    description: t('seo.confidentialite_description'),
    imageUrl: OG_IMAGE_URL,
    url: pageUrl,
    type: 'website',
  })

  return (
    <LegalPageLayout
      title={t('legal.privacy.page_title')}
      lastUpdatedLabel={formatLastUpdated(lang)}
    >
      <section>
        <h2>{t('legal.privacy.intro_title')}</h2>
        <p>{t('legal.privacy.intro_body')}</p>
      </section>

      <section>
        <h2>{t('legal.privacy.controller_title')}</h2>
        <p>
          {t('legal.privacy.controller_body')}{' '}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
        </p>
      </section>

      <section>
        <h2>{t('legal.privacy.data_title')}</h2>
        <p>{t('legal.privacy.data_intro')}</p>

        <h2 className="!text-base">{t('legal.privacy.data_account_title')}</h2>
        <p>{t('legal.privacy.data_account_body')}</p>

        <h2 className="!text-base">{t('legal.privacy.data_public_activity_title')}</h2>
        <p>{t('legal.privacy.data_public_activity_body')}</p>

        <h2 className="!text-base">{t('legal.privacy.data_uploads_title')}</h2>
        <p>{t('legal.privacy.data_uploads_body')}</p>

        <h2 className="!text-base">{t('legal.privacy.data_recaptcha_title')}</h2>
        <p>{t('legal.privacy.data_recaptcha_body')}</p>
      </section>

      <section>
        <h2>{t('legal.privacy.legal_basis_title')}</h2>
        <ul>
          <li>{t('legal.privacy.legal_basis_account')}</li>
          <li>{t('legal.privacy.legal_basis_media')}</li>
          <li>{t('legal.privacy.legal_basis_recaptcha')}</li>
        </ul>
      </section>

      <section>
        <h2>{t('legal.privacy.retention_title')}</h2>
        <p>{t('legal.privacy.retention_body')}</p>
      </section>

      <section>
        <h2>{t('legal.privacy.recipients_title')}</h2>
        <p>{t('legal.privacy.recipients_intro')}</p>
        <ul>
          <li>{t('legal.privacy.recipients_vercel')}</li>
          <li>{t('legal.privacy.recipients_koyeb')}</li>
          <li>{t('legal.privacy.recipients_neon')}</li>
          <li>{t('legal.privacy.recipients_google')}</li>
          <li>{t('legal.privacy.recipients_twitch')}</li>
        </ul>
      </section>

      <section>
        <h2>{t('legal.privacy.transfers_title')}</h2>
        <p>{t('legal.privacy.transfers_body')}</p>
      </section>

      <section>
        <h2>{t('legal.privacy.rights_title')}</h2>
        <p>{t('legal.privacy.rights_intro')}</p>
        <ul>
          <li>{t('legal.privacy.rights_access')}</li>
          <li>{t('legal.privacy.rights_rectification')}</li>
          <li>{t('legal.privacy.rights_erasure')}</li>
          <li>{t('legal.privacy.rights_restriction')}</li>
          <li>{t('legal.privacy.rights_portability')}</li>
          <li>{t('legal.privacy.rights_objection')}</li>
          <li>{t('legal.privacy.rights_withdraw_consent')}</li>
        </ul>
      </section>

      <section>
        <h2>{t('legal.privacy.complaint_title')}</h2>
        <p>{t('legal.privacy.complaint_body')}</p>
        <p>
          CNIL — {t('legal.privacy.complaint_address')}
          <br />
          <a href={t('legal.privacy.complaint_website')} target="_blank" rel="noopener noreferrer">
            {t('legal.privacy.complaint_website')}
          </a>
        </p>
      </section>
    </LegalPageLayout>
  )
}
